import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function ToValidate() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [motif, setMotif] = useState("");
  const canValidate = ["ADMIN", "VALIDATEUR"].includes(user?.Role);

  const load = useCallback(() => {
    api.get("/protocoles", { params: { statut: "A_VALIDER" } }).then((res) => setProtocols(res.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const validate = async (id) => {
    await api.post(`/protocoles/${id}/valider`);
    toast.success(t("common.validate"));
    load();
  };
  const refuse = async (id) => {
    await api.post(`/protocoles/${id}/refuser`, { motif });
    toast.success(t("common.refuse"));
    setMotif("");
    load();
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="to-validate-view">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.to_validate")}</h1>
      {protocols.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="to-validate-empty">
          {t("common.no_results")}
        </p>
      ) : (
        <div className="space-y-3">
          {protocols.map((p) => (
            <div
              key={p.Protocol_ID}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3"
              data-testid={`to-validate-row-${p.Protocol_ID}`}
            >
              <div>
                <Link to={`/protocole/${p.Protocol_ID}`} className="font-mono text-xs text-amber-600 dark:text-amber-400 hover:underline">
                  {p.Protocol_ID}
                </Link>
                <p className="font-semibold text-slate-800 dark:text-slate-200">{p.Titre}</p>
                <p className="text-xs text-slate-500">
                  {p.Site_Nom} · v{p.Version_Courante}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={p.Statut} />
                {canValidate && (
                  <>
                    <Button size="sm" data-testid={`btn-validate-${p.Protocol_ID}`} onClick={() => validate(p.Protocol_ID)}>
                      {t("common.validate")}
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="destructive" data-testid={`btn-refuse-${p.Protocol_ID}`}>
                          {t("common.refuse")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t("common.refuse")}</DialogTitle>
                        </DialogHeader>
                        <Textarea data-testid="input-quick-refuse-motif" placeholder={t("common.reason_required")} value={motif} onChange={(e) => setMotif(e.target.value)} />
                        <DialogFooter>
                          <Button data-testid="btn-confirm-quick-refuse" onClick={() => refuse(p.Protocol_ID)}>
                            {t("common.confirm")}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
